import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { STATS } from '../data';
import { useCountUp } from '../hooks/useCountUp';

function StatCard({ value, suffix, label, i }) {
  const ref     = useRef(null);
  const inView  = useInView(ref, { once:true });
  const count   = useCountUp(value, inView);
  return (
    <motion.div ref={ref}
      initial={{ opacity:0, y:28 }} whileInView={{ opacity:1, y:0 }}
      transition={{ delay:i*0.1, duration:0.55 }} viewport={{ once:true }}
      whileHover={{ scale:1.06, y:-4 }}
      className="glass rounded-2xl p-6 text-center border border-white/[0.06] card-lift"
    >
      <p className="text-4xl font-black grad-text mb-1">{count}{suffix}</p>
      <p className="text-slate-400 text-sm font-medium">{label}</p>
    </motion.div>
  );
}

const TIMELINE = [
  { year:'2017', text:'Started freelance development journey', icon:'🌱' },
  { year:'2018', text:'Joined nonprofit as IT Officer & Developer', icon:'🏢' },
  { year:'2020', text:'Moved to full-stack role at Digital Agency', icon:'⚡' },
  { year:'2021', text:'Mastered React.js ecosystem and TypeScript', icon:'⚛️' },
  { year:'2022', text:'Promoted to Senior Full-Stack Developer', icon:'🚀' },
  { year:'2024', text:'Delivered OwnAttend HRMS & CONE Platform', icon:'🏆' },
];

const INFO = [
  { label:'Location', value:'Addis Ababa, ET 🇪🇹' },
  { label:'Focus',    value:'Full-Stack & Enterprise' },
  { label:'Stack',    value:'React + Laravel' },
  { label:'Status',   value:'✅ Available' },
];

export default function About() {
  return (
    <section id="about" className="section-pad relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-violet-950/10 to-transparent pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <motion.div initial={{opacity:0,y:28}} whileInView={{opacity:1,y:0}}
          transition={{duration:0.55}} viewport={{once:true}} className="text-center mb-20">
          <p className="text-violet-400 font-mono text-xs tracking-[0.2em] uppercase mb-4">— About Me</p>
          <h2 className="text-4xl sm:text-5xl font-black text-white mb-4">
            The Developer Behind <span className="grad-text">the Code</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            Turning complex business requirements into elegant, scalable software
          </p>
        </motion.div>

        {/* 2-column body */}
        <div className="grid lg:grid-cols-2 gap-14 items-start mb-20">

          {/* Bio card */}
          <motion.div initial={{opacity:0,x:-36}} whileInView={{opacity:1,x:0}}
            transition={{duration:0.65}} viewport={{once:true}}>
            <div className="glass rounded-3xl p-8 border border-white/[0.06]">
              <h3 className="text-2xl font-bold text-white mb-5 flex items-center gap-3">
                <span>👋</span> Hello, I'm Mustafa
              </h3>
              <div className="space-y-4 text-slate-400 leading-relaxed text-[15px]">
                <p>
                  I'm a <span className="text-violet-400 font-semibold">Senior Full-Stack Developer</span> based in
                  Addis Ababa, Ethiopia, with 7+ years building enterprise-grade web applications that solve real problems.
                </p>
                <p>
                  My work spans the full stack — from architecting MySQL schemas and RESTful Laravel APIs, to crafting
                  performant React frontends. I specialise in{' '}
                  <span className="text-cyan-400 font-semibold">HRMS, ERP, and complex enterprise systems</span>{' '}
                  that handle real organisational workflows.
                </p>
                <p>
                  Beyond code I'm passionate about clean architecture, developer experience, and building systems
                  teams actually enjoy using. I'm currently exploring AI integrations that give clients an edge.
                </p>
              </div>

              {/* Info grid */}
              <div className="mt-7 grid grid-cols-2 gap-3">
                {INFO.map(({ label, value }) => (
                  <div key={label} className="bg-white/[0.03] rounded-xl p-3 border border-white/[0.05]">
                    <p className="text-slate-600 text-xs mb-0.5">{label}</p>
                    <p className="text-white text-sm font-medium">{value}</p>
                  </div>
                ))}
              </div>

              <motion.a href="#contact" whileHover={{scale:1.02}} whileTap={{scale:0.97}}
                className="mt-7 w-full py-3 text-center font-semibold text-white rounded-xl btn-primary block">
                Get In Touch
              </motion.a>
            </div>
          </motion.div>

          {/* Timeline */}
          <motion.div initial={{opacity:0,x:36}} whileInView={{opacity:1,x:0}}
            transition={{duration:0.65}} viewport={{once:true}}>
            <h3 className="text-lg font-bold text-white mb-7 flex items-center gap-2">
              <span className="text-violet-400 font-mono text-sm">📅</span> My Journey
            </h3>
            <div className="relative">
              <div className="absolute left-5 top-0 bottom-0 w-px tl-line opacity-30" />
              <div className="space-y-5">
                {TIMELINE.map((item, i) => (
                  <motion.div key={item.year}
                    initial={{opacity:0,x:16}} whileInView={{opacity:1,x:0}}
                    transition={{delay:i*0.08,duration:0.45}} viewport={{once:true}}
                    className="flex items-start gap-4 pl-2"
                  >
                    <div className="relative z-10 w-7 h-7 rounded-full bg-dark-700 border border-violet-500/30 flex items-center justify-center text-sm flex-shrink-0">
                      {item.icon}
                    </div>
                    <div className="glass rounded-xl p-3.5 flex-1 border border-white/[0.05] hover:border-violet-500/25 transition-colors">
                      <span className="text-violet-400 font-mono text-xs font-bold block mb-0.5">{item.year}</span>
                      <p className="text-slate-300 text-sm">{item.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STATS.map((s, i) => <StatCard key={s.label} {...s} i={i} />)}
        </div>
      </div>
    </section>
  );
}
